import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    AppBar, Avatar, Box, Button, Chip, Divider, Drawer, IconButton, List, ListItem, ListItemButton,
    ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, Toolbar, Tooltip, Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/SpaceDashboard';
import StarIcon from '@mui/icons-material/Star';
import DiscountIcon from '@mui/icons-material/Discount';
import NotificationsIcon from '@mui/icons-material/Notifications';
import StyleIcon from '@mui/icons-material/Style';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PolicyIcon from '@mui/icons-material/Policy';
import GavelIcon from '@mui/icons-material/Gavel';
import ReceiptIcon from '@mui/icons-material/Receipt';

const corPrimaria = "#0b243d";
const corDestaque = "#ffc845";

// Itens do menu lateral, agrupados. "atalho" também aparece na barra superior em telas grandes.
const secoesMenu = [
    {
        titulo: "Principal",
        itens: [
            { label: "Dashboard", caminho: "/Dashboard", icone: <DashboardIcon />, atalho: true },
            { label: "Emitir Nota Fiscal", rotulo: "Emitir Nota", caminho: "/EmitirNota", icone: <ReceiptIcon />, atalho: true },
        ],
    },
    {
        titulo: "Comercial",
        itens: [
            { label: "Assinaturas", caminho: "/Assinaturas", icone: <StarIcon />, atalho: true },
            { label: "Planos", caminho: "/Planos", icone: <StyleIcon /> },
            { label: "Descontos", caminho: "/Descontos", icone: <DiscountIcon />, relacionados: ["/DescontosUsados"] },
        ],
    },
    {
        titulo: "Gestão",
        itens: [
            { label: "Usuários", caminho: "/Usuarios", icone: <PeopleAltIcon />, atalho: true, relacionados: ["/Admins"] },
            { label: "Notificações", icone: <NotificationsIcon />, emBreve: true },
        ],
    },
    {
        titulo: "Legal",
        itens: [
            { label: "Termos de uso", caminho: "/TermosDeUso", icone: <GavelIcon /> },
            { label: "Políticas de Privacidade", caminho: "/PoliticaDePrivacidade", icone: <PolicyIcon /> },
        ],
    },
];

const atalhos = secoesMenu.flatMap((secao) => secao.itens).filter((item) => item.atalho);

// O e-mail do admin vem no payload do JWT (só para exibição; a validação é feita no back)
const emailDoToken = (token) => {
    try {
        const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        return JSON.parse(atob(payload)).email || "";
    } catch {
        return "";
    }
};

function Header() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [menuAberto, setMenuAberto] = useState(false);
    const [ancoraConta, setAncoraConta] = useState(null);
    const emailAdmin = emailDoToken(localStorage.getItem('token') || "");

    useEffect(() => {
        const verificarSessao = async () => {
            const token = localStorage.getItem('token');
            if (!token) return navigate("/");

            try {
                const response = await axios.post(`${process.env.REACT_APP_API_URL}/verificarTokenAdmin`, {}, {
                    headers: { Authorization: token }
                });
                if (response.data.status !== 200) {
                    localStorage.removeItem('token');
                    navigate("/");
                }
            } catch (err) {
                // Falha de rede não derruba a sessão; as próprias páginas tratam o erro
                console.error("Erro ao verificar sessão:", err);
            }
        };
        verificarSessao();
    }, [navigate]);

    // Também marca o item em páginas que pertencem a ele (ex.: /Admins fica em "Usuários")
    const estaAtivo = (caminho) => {
        if (!caminho) return false;
        const item = secoesMenu.flatMap((secao) => secao.itens).find((i) => i.caminho === caminho);
        return [caminho, ...(item?.relacionados || [])].some((c) => pathname === c || pathname.startsWith(`${c}/`));
    };

    const irPara = (caminho) => {
        setMenuAberto(false);
        navigate(caminho);
    };

    const sair = () => {
        setAncoraConta(null);
        setMenuAberto(false);
        localStorage.removeItem('token');
        navigate("/");
    };

    const marca = (
        <Box
            onClick={() => irPara("/Dashboard")}
            sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", userSelect: "none" }}
        >
            <Typography sx={{ fontWeight: 700, fontSize: 20, letterSpacing: 0.3 }}>Clarea</Typography>
            <Chip
                label="ADM"
                size="small"
                sx={{ height: 20, fontSize: 11, fontWeight: 700, backgroundColor: corDestaque, color: corPrimaria }}
            />
        </Box>
    );

    return (
        <>
            <AppBar
                position="sticky"
                elevation={0}
                sx={{ backgroundColor: corPrimaria, borderBottom: "1px solid rgba(255,255,255,0.08)" }}
            >
                <Toolbar sx={{ gap: 1, minHeight: { xs: 56, sm: 64 } }}>
                    <IconButton edge="start" color="inherit" aria-label="Abrir menu" onClick={() => setMenuAberto(true)}>
                        <MenuIcon />
                    </IconButton>

                    {marca}

                    {/* Atalhos das páginas mais usadas (telas grandes) */}
                    <Box component="nav" sx={{ display: { xs: "none", md: "flex" }, gap: 0.5, ml: 4, flexGrow: 1 }}>
                        
                    </Box>
                    <Box sx={{ flexGrow: 1, display: { xs: "block", md: "none" } }} />

                    {/* Conta do admin */}
                    <Tooltip title={emailAdmin || "Conta"}>
                        <IconButton onClick={(e) => setAncoraConta(e.currentTarget)} aria-label="Conta" sx={{ p: 0.5 }}>
                            <Avatar sx={{ width: 34, height: 34, fontSize: 15, backgroundColor: corDestaque, color: corPrimaria, fontWeight: 700 }}>
                                {(emailAdmin[0] || "A").toUpperCase()}
                            </Avatar>
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={ancoraConta}
                        open={!!ancoraConta}
                        onClose={() => setAncoraConta(null)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        slotProps={{ paper: { sx: { mt: 1, minWidth: 220 } } }}
                    >
                        <Box sx={{ px: 2, py: 1 }}>
                            <Typography variant="caption" color="text.secondary">Conectado como</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: "break-all" }}>{emailAdmin || "Administrador"}</Typography>
                        </Box>
                        <Divider />
                        <MenuItem onClick={sair} sx={{ color: "error.main", mt: 0.5 }}>
                            <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
                            Sair
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            <Drawer open={menuAberto} onClose={() => setMenuAberto(false)} slotProps={{ paper: { sx: { width: 280 } } }}>
                <Box sx={{ backgroundColor: corPrimaria, color: "#fff", px: 2.5, minHeight: { xs: 56, sm: 64 }, display: "flex", alignItems: "center" }}>
                    {marca}
                </Box>

                <Box component="nav" sx={{ flex: 1, overflowY: "auto", py: 1 }}>
                    {secoesMenu.map((secao) => (
                        <List
                            key={secao.titulo}
                            dense
                            subheader={
                                <ListSubheader sx={{ lineHeight: "32px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, backgroundColor: "transparent" }}>
                                    {secao.titulo}
                                </ListSubheader>
                            }
                        >
                            {secao.itens.map((item) => {
                                const ativo = estaAtivo(item.caminho);
                                return (
                                    <ListItem key={item.label} disablePadding sx={{ px: 1 }}>
                                        <ListItemButton
                                            selected={ativo}
                                            disabled={item.emBreve}
                                            onClick={() => irPara(item.caminho)}
                                            sx={{
                                                borderRadius: 1.5,
                                                py: 0.9,
                                                "&.Mui-selected": { backgroundColor: "rgba(11,36,61,0.08)" },
                                                "&.Mui-selected:hover": { backgroundColor: "rgba(11,36,61,0.12)" },
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 38, color: ativo ? corPrimaria : "text.secondary" }}>
                                                {item.icone}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item.label}
                                                slotProps={{ primary: { sx: { fontSize: 14, fontWeight: ativo ? 600 : 400, color: ativo ? corPrimaria : "text.primary" } } }}
                                            />
                                            {item.emBreve && <Chip label="Em breve" size="small" sx={{ height: 20, fontSize: 10 }} />}
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    ))}
                </Box>

                <Divider />
                <Box sx={{ p: 1.5 }}>
                    {emailAdmin && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 1, mb: 1, wordBreak: "break-all" }}>
                            {emailAdmin}
                        </Typography>
                    )}
                    <ListItemButton onClick={sair} sx={{ borderRadius: 1.5, color: "error.main" }}>
                        <ListItemIcon sx={{ minWidth: 38 }}><LogoutIcon color="error" /></ListItemIcon>
                        <ListItemText primary="Sair" slotProps={{ primary: { sx: { fontSize: 14 } } }} />
                    </ListItemButton>
                </Box>
            </Drawer>
        </>
    );
}

export default Header;
